// Standalone sticky buy bar: shows after scrolling past ~30% of the viewport height,
// hides near footer (or once a configured anchor section is reached). Kept separate
// from theme.js's ProductStickyForm (which triggers off the main product form).
class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    this.card = this.firstElementChild;
    if (!this.card) return;

    this.visible = false;
    this.footerReached = false;
    this.useAnchor = false;
    this.onScroll = this.onScroll.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    const anchorId = this.dataset.hideAnchorId;
    this.anchorElement = anchorId ? document.getElementById(anchorId) : null;

    if (this.anchorElement) {
      // Anchor position is re-measured on every scroll frame in update(), so hiding
      // reacts as soon as the anchor's top edge reaches the top of the viewport
      // (and stays hidden past it, regardless of how tall the anchor section is).
      this.useAnchor = true;
    } else if ('IntersectionObserver' in window) {
      const footer = document.querySelector('.footer-group');
      if (footer) {
        this.footerObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => { this.footerReached = entry.isIntersecting; });
          this.update();
        });
        this.footerObserver.observe(footer);
      }
    }

    this.onBuyNowClick = this.onBuyNowClick.bind(this);
    this.addEventListener('click', this.onBuyNowClick);

    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    this.footerObserver?.disconnect();
    this.removeEventListener('click', this.onBuyNowClick);
  }

  onBuyNowClick(event) {
    const button = event.target.closest('[data-buy-now-variant]');
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.dataset.buyNowVariant;
    const quantity = parseInt(button.dataset.buyNowQuantity, 10) || 1;
    if (!variantId) return;

    // Cart permalink → checkout with selected variant + qty
    window.location.href = `${theme.routes.cart_url}/${variantId}:${quantity}`;
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    const scrolledEnough = window.scrollY > window.innerHeight * 0.3;
    const shouldHide = this.useAnchor
      ? this.anchorElement.getBoundingClientRect().top <= 0
      : this.footerReached;
    const shouldShow = scrolledEnough && !shouldHide;
    if (shouldShow === this.visible) return;
    this.visible = shouldShow;

    if (window.Motion && typeof Motion.animate === 'function') {
      Motion.animate(
        this.card,
        shouldShow
          ? { opacity: 1, visibility: 'visible', transform: ['translateY(.9375rem)', 'translateY(0)'] }
          : { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(.9375rem)'] },
        { duration: 1, easing: [0.16, 1, 0.3, 1] }
      );
    } else {
      this.card.style.opacity = shouldShow ? '1' : '0';
      this.card.style.visibility = shouldShow ? 'visible' : 'hidden';
    }
  }
}

if (!customElements.get('sticky-buy-bar')) {
  customElements.define('sticky-buy-bar', StickyBuyBar);
}
